import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Stack,
  TextField,
  InputAdornment,
  CircularProgress,
  Typography,
  Alert,
} from "@mui/material";
import { SearchRounded, ArticleOutlined } from "@mui/icons-material";

import CustomAppBar from "../../../components/CustomAppBar";

import InfoCard from "../components/InfoCard";
import { customerService } from "../services/customerService";
import {
  bgGradient,
  textPrimary,
  textSecondary,
  cardShadow,
  MESSAGES,
} from "../../../constants/consts";

const normalize = (s = "") => s.toLocaleLowerCase("tr-TR").trim();

export default function CustomerInfo({ user, onLogout }) {
  const [list, setList] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await customerService.getInformationBank();
        setList(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        setError(MESSAGES.INFO_LOAD_ERROR);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Arama — title + description içinde arar
  const filtered = useMemo(() => {
    const q = normalize(query);
    if (!q) return list;
    // Flutter'daki serchForAnInfo ile aynı: sadece title'da arar
    return list.filter((i) => normalize(i.title).includes(q));
  }, [list, query]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: bgGradient,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.10)",
          top: -100,
          right: -100,
        },
        "&::after": {
          content: '""',
          position: "absolute",
          width: 300,
          height: 300,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.08)",
          bottom: -80,
          left: -80,
        },
      }}
    >
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <CustomAppBar
          title={MESSAGES.INFO_TITLE}
          subtitle={MESSAGES.INFO_SUBTITLE}
          isNotHome
          onLogout={onLogout}
        />

        <Container
          sx={{
            pt: { xs: "80px", sm: "96px" },
            //  pt: { xs: 2, sm: 5 },
            minHeight: "calc(100vh - 64px)",
          }}
        >
          {/* Arama */}
          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              backgroundColor: "#fff",
              boxShadow: cardShadow,
              mb: 3,
            }}
          >
            <TextField
              fullWidth
              placeholder={MESSAGES.INFO_SEARCH_PLACEHOLDER}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRounded />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress sx={{ color: "#fff" }} />
            </Box>
          ) : filtered.length === 0 ? (
            <EmptyState
              title={MESSAGES.INFO_EMPTY_TITLE}
              message={MESSAGES.INFO_EMPTY_MESSAGE}
            />
          ) : (
            <Stack spacing={2}>
              {filtered.map((item) => (
                <InfoCard key={item.id} info={item} />
              ))}
            </Stack>
          )}
        </Container>
      </Box>
    </Box>
  );
}

function EmptyState({ title, message }) {
  return (
    <Box
      sx={{
        p: 5,
        textAlign: "center",
        borderRadius: 3,
        backgroundColor: "rgba(255,255,255,0.94)",
        boxShadow: cardShadow,
      }}
    >
      <Box
        sx={{
          width: 72,
          height: 72,
          mx: "auto",
          mb: 2,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(0,0,0,0.04)",
        }}
      >
        <ArticleOutlined sx={{ fontSize: 36, color: textSecondary }} />
      </Box>
      <Typography
        variant="h6"
        fontWeight={700}
        color={textPrimary}
        gutterBottom
      >
        {title}
      </Typography>
      <Typography variant="body2" color={textSecondary}>
        {message}
      </Typography>
    </Box>
  );
}
